import { Flex, Form, Input, message, Modal, Typography, Rate } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { CustomButton } from '@/components/ui';
import './index.scss';
import { FloatingLabel } from '@/components/ui/FloatingLabel';
import { CustomTextarea } from '@/components/ui/CustomTextarea';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Booking } from '@/types';
import { reviewBooking, reviewBookingRequest } from '@/api/booking';
import { updateSession } from '@/api/admin';

import { notifyActionError } from '@/helpers/subscriptionError';
interface Props {
  booking: Booking;
  open: boolean;
  onClose?: () => void;
  onSkip?: () => void;
  isAdminView: boolean;
  type: string;
}

export const ReviewModal = ({
  open,
  booking,
  onClose,
  onSkip,
  isAdminView = false,
  type = '',
}: Props) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(open);
  const queryClient = useQueryClient();
  const { mutateAsync: requestedReview } = useMutation({
    mutationFn: async () => {
      return await reviewBookingRequest(booking.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['bookings'],
      });
      // TCX-790: also refresh the scoped booking DETAIL (['booking', ...]).
      queryClient.invalidateQueries({
        queryKey: ['booking'],
      });
      queryClient.invalidateQueries({
        queryKey: ['latest-session'],
      });
    },
  });

  useEffect(() => {
    if (open) {
      setIsOpen(open);
    }
  }, [open]);

  useEffect(() => {
    if (isAdminView) {
      form.setFieldsValue({
        rating:
          Array.isArray(booking?.review) && booking.review.length > 0
            ? booking.review[0].rating
            : 0,
        comment:
          Array.isArray(booking?.review) && booking.review.length > 0
            ? booking.review[0].comment
            : '',
      });
    }
  }, [booking, isAdminView]);

  const { mutateAsync: reviewBookingMutation } = useMutation({
    mutationFn: async (data: { rating: number; comment?: string }) => {
      return await reviewBooking(booking.id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['bookings'],
      });
      queryClient.invalidateQueries({
        queryKey: ['booking'],
      });
      queryClient.invalidateQueries({
        queryKey: ['latest-session'],
      });
      message.success('Thank you for your feedback.');
    },
    onError: (error: any) => {
      // TCX-900: keeps a retryable Stripe outage from reading as a rejection.
      notifyActionError(error);
    },
  });

  const { mutateAsync: updateRatingByAdmin } = useMutation({
    mutationFn: async (data: { rating: number; comment?: string }) => {
      return await updateSession(booking._id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['bookings'],
      });
      queryClient.invalidateQueries({
        queryKey: ['booking'],
      });
      queryClient.invalidateQueries({
        queryKey: ['latest-session'],
      });
      message.success('Thank you for your feedback.');
    },
    onError: (error: any) => {
      // TCX-900: keeps a retryable Stripe outage from reading as a rejection.
      notifyActionError(error);
    },
  });

  const formatDate = (date: Date) => {
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();

    let hours = date.getHours();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;

    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${month} ${day}, ${year}, at ${hours}:${minutes} ${ampm}`;
  };

  // TCX-916: the meeting's scheduled start, not `createdAt` (when it was
  // booked). `createdAt` stays only as a fallback for a session without one.
  const formattedDate = formatDate(
    new Date(booking?.start || booking?.createdAt || '') || new Date(),
  );

  const handleClose = async () => {
    // await requestedReview(); // This is to change bcz to show every time rating modal to franchisor for giving review
    if (onClose) {
      onClose();
    }
    setIsOpen(false);
  };

  const handleSkip = async () => {
    if (onSkip) {
      onSkip();
    }
    if (onClose) {
      onClose();
    }
    setIsOpen(false);
  };

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      if (isAdminView && type === 'edit') {
        await updateRatingByAdmin(values);
      } else {
        await reviewBookingMutation(values);
      }
      form.resetFields();
      handleClose();
    } catch {
      // mutation onError already surfaced the message; keep the modal open.
    } finally {
      setLoading(false);
    }
  };
  return (
    <Modal
      closeIcon={<CloseOutlined />}
      open={isOpen}
      destroyOnClose={true}
      onCancel={loading ? undefined : handleClose}
      maskClosable={!loading}
      keyboard={!loading}
      width={460}
      centered
      footer={null}
      className={'review-booking-modal'}
      styles={{
        mask: {
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        },
      }}
    >
      <Flex vertical className={'header'} align={'start'} gap={16}>
        <Typography.Text className={'title'}>
          {`Rate Session with ${booking?.consultant?.firstName} ${booking?.consultant?.lastName}`}
        </Typography.Text>

        <Typography.Text className={'description'}>
          {`${
            isAdminView ? booking?.franchise?.company : 'You'
          } had a session with ${booking?.consultant?.firstName} ${
            booking?.consultant?.lastName
          } on ${formattedDate}. Please share your feedback to help TCX enhance the experience for franchisors like you and ensure the highest-quality consultant connections.`}
        </Typography.Text>
        <Typography.Text className={'description'}>
          All ratings remain anonymous.
        </Typography.Text>
      </Flex>

      <Form layout="vertical" name="basic" onFinish={onFinish} form={form}>
        <FloatingLabel label="Email" childrenType="input">
          <Form.Item
            name="rating"
            rules={[{ required: true, message: 'Please input your rating!' }]}
          >
            <Rate
              style={{ color: '#2a4bc2', fontSize: '36px' }}
              allowClear={false}
              disabled={isAdminView && type === 'view'}
            />
          </Form.Item>
        </FloatingLabel>

        <FloatingLabel label="Comment" childrenType="textarea">
          <Form.Item name="comment" label="Comment">
            <CustomTextarea
              placeholder="Please share the details of your experience."
              disabled={isAdminView && type === 'view'}
            />
          </Form.Item>
        </FloatingLabel>
      </Form>

      <Flex vertical gap={12}>
        {isAdminView && type === 'edit' && (
          <CustomButton
            onClick={() => form.submit()}
            type={'primary'}
            text={'UPDATE'}
            disabled={loading}
          />
        )}
        {!isAdminView && (
          <CustomButton
            onClick={() => form.submit()}
            type={'primary'}
            text={'POST'}
            disabled={loading}
          />
        )}

        <CustomButton
          type={'default'}
          onClick={handleSkip}
          text={isAdminView ? 'CLOSE' : 'SKIP'}
          disabled={loading}
        />
      </Flex>
    </Modal>
  );
};
